import debounce from 'lodash/debounce';
import type { ChangeEvent, KeyboardEvent } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchBox } from 'react-instantsearch';
import { useIntl } from 'react-intl';

import { useAlgoliaUserToken } from '@/hooks/useAlgoliaUserToken';
import { useClickOutside } from '@/hooks/useClickOutside';
import { useGoogleAnalytics } from '@/hooks/useGoogleAnalytics';
import { useRouter } from '@/hooks/useRouter';
import {
  applyProductConfigurationFilters,
  getWatchFilters,
  performMultiSearch,
  ProductConfigurationFilterType,
} from '@/integrations/algolia/helpers';
import type { SearchHit } from '@/integrations/algolia/types';
import { DEBOUNCE_TIME_IN_MS, HITS_PER_PAGE } from '@/lib/const';
import { EVENT_NAME } from '@/lib/gtm/const';
import type { WithHTMLProp } from '@/lib/types';
import { useRegionProvider } from '@/providers/RegionProvider';
import { useSearchState } from '@/states/searchState';

import { SearchDropdown } from './SearchDropdown';
import { getGroupKey, getGroupLabel, getHitName } from './utils';

import * as S from './AlgoliaSearchInput.styles';
import * as M from './messages';

interface AlgoliaSearchInputProps extends WithHTMLProp<'className'> {
  clearOnUnmount?: boolean;
  draftMode: boolean;
  placeholder?: string | null;
}

export const AlgoliaSearchInput = ({
  className,
  clearOnUnmount = false,
  draftMode,
}: AlgoliaSearchInputProps) => {
  const { formatMessage } = useIntl();
  const { overlayStatus } = useSearchState();

  const { query, refine } = useSearchBox();
  const { locale, paths, push } = useRouter(); // when bowser click back button, query do not change
  const { locale: regionLocale } = useRegionProvider();
  const {
    country: { code },
  } = useRegionProvider();
  const { isLoading: isUserTokenLoading, userToken } = useAlgoliaUserToken();
  const [inputValue, setInputValue] = useState<string>(query);

  const [isFocused, setIsFocused] = useState(false);
  const [hits, setHits] = useState<SearchHit[]>([]);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const loadingRef = useRef<boolean>(false);

  const { sendEvent } = useGoogleAnalytics();

  //sync query, inputValue
  useEffect(() => {
    if (inputValue !== query) {
      setInputValue(query);
    }
  }, [query]);

  useClickOutside(inputRef, () => {
    setIsFocused(false);
  });

  const doSearch = useCallback(
    async (searchTerm: string) => {
      refine(searchTerm);

      // Defer navigation to yield to main thread for immediate UI response
      setTimeout(async () => {
        const queryParams: Record<string, string> = {};
        if (searchTerm) {
          queryParams['q'] = searchTerm;
        }

        await push(paths.search.all.url({ query: queryParams }));
        loadingRef.current = false;
      }, 0);
    },
    [paths, push, refine],
  );

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      sendEvent({
        event: EVENT_NAME.SEARCH,
        search_term: inputValue,
      });

      inputRef?.current?.blur();
      doSearch(inputValue);
    }
  };

  const [isWatchSku, setIsWatchSku] = useState(false);
  const handleIconClick = () => {
    if (loadingRef.current) return;
    loadingRef.current = true;
    sendEvent({
      event: EVENT_NAME.SEARCH,
      search_term: inputValue,
    });

    doSearch(inputValue);
  };

  const handleHintClick = useCallback(
    (hit: SearchHit) => {
      if (!isWatchSku) {
        const hitName = getHitName(hit, regionLocale);
        if (hitName) {
          setInputValue(hitName);
          doSearch(hitName);
        } else {
          doSearch(inputValue);
        }
      } else {
        doSearch(inputValue);
      }
    },
    [inputValue, isWatchSku, doSearch, regionLocale],
  );

  const searchDepsRef = useRef({
    code,
    draftMode,
    isUserTokenLoading,
    locale,
    regionLocale,
    userToken,
  });

  useEffect(() => {
    searchDepsRef.current = {
      code,
      draftMode,
      isUserTokenLoading,
      locale,
      regionLocale,
      userToken,
    };
  }, [code, draftMode, isUserTokenLoading, locale, regionLocale, userToken]);

  const handleChange = useMemo(
    () =>
      debounce(async (value: string) => {
        const {
          code: currentCode,
          draftMode: currentDraftMode,
          isUserTokenLoading: currentIsUserTokenLoading,
          locale: currentLocale,
          regionLocale: currentRegionLocale,
          userToken: currentUserToken,
        } = searchDepsRef.current;

        // Don't perform search if userToken is still loading
        if (currentIsUserTokenLoading) {
          return;
        }

        const defaultFilters = applyProductConfigurationFilters(
          ProductConfigurationFilterType.SEARCH,
          undefined,
          currentDraftMode,
        );
        const [watchFilters, isWatchSkuArray] = getWatchFilters(
          value,
          defaultFilters,
        );
        const localIsWatchSku =
          isWatchSkuArray.length > 0 ? isWatchSkuArray[0] : false;
        setIsWatchSku(localIsWatchSku);
        const filters = localIsWatchSku ? watchFilters : defaultFilters;
        const queryType = 'prefixAll';
        const typoTolerance = 'min';
        const { results } = await performMultiSearch(
          value,
          currentLocale,
          {
            hitsPerPage: HITS_PER_PAGE,
            filters: filters,
          },
          currentDraftMode,
          currentCode,
          queryType,
          typoTolerance,
          currentUserToken,
        );
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const hits = results.flatMap(({ hits }: any) => hits);

        const uniqueByNameMap = new Map<string, SearchHit>();

        for (const hit of hits) {
          let name = '';

          if (
            hit.product_type === 'Watch' ||
            hit.product_type === 'Strap' ||
            hit.product_type === 'Eyewear'
          ) {
            const watchName = hit.name[currentRegionLocale.code.toUpperCase()];
            name = typeof watchName === 'string' ? watchName : '';
          } else if (hit.type && typeof hit.type === 'string') {
            name = getGroupLabel(hit.type);
          } else {
            name = 'Others';
          }
          if (name && !uniqueByNameMap.has(name)) {
            uniqueByNameMap.set(name, hit);
          }
        }

        const uniqueHits = Array.from(uniqueByNameMap.values());

        setHits(uniqueHits);
      }, DEBOUNCE_TIME_IN_MS),
    [],
  );

  useEffect(() => {
    return () => {
      handleChange.cancel();
    };
  }, [handleChange]);

  useEffect(() => {
    // NOSONAR: long existing logic, works as expected
    if (overlayStatus === 'CLOSE' && clearOnUnmount) {
      setInputValue('');
    } else if (overlayStatus === 'OPEN') {
      inputRef.current?.focus();
    }
  }, [overlayStatus]);

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (!isFocused) {
      setIsFocused(true);
    }
    setInputValue(e.target.value);
    handleChange(e.target.value);
  };

  const groupedHits = useMemo(() => {
    const groupingMap = new Map<string, SearchHit[]>();

    hits.forEach((hit) => {
      const key = getGroupKey(hit);
      if (!groupingMap.has(key)) {
        groupingMap.set(key, [hit]);
      } else {
        groupingMap.get(key)!.push(hit);
      }
    });

    return Array.from(groupingMap.entries()).map(([groupKey, hits]) => ({
      groupKey,
      hits,
    }));
  }, [hits]);

  return (
    <S.Wrapper className={className} data-test-id='AlgoliaSearchInputWrapper'>
      <S.InputWrapper data-test-id='AlgoliaSearchInputInputWrapper'>
        <S.Input
          ref={inputRef}
          data-test-id='AlgoliaSearchInputInput'
          onChange={handleInputChange}
          onClick={() => setIsFocused(true)}
          onFocus={() => setIsFocused(true)}
          onKeyDown={handleKeyDown}
          placeholder={formatMessage(M.defined.whatAreYouLookingFor)}
          value={inputValue}
        />
        <S.SearchIcon
          data-testid='AlgoliaSearchInputSearchIcon'
          onClick={handleIconClick}
        />
      </S.InputWrapper>

      {isFocused && inputValue && (
        <SearchDropdown
          draftMode={draftMode}
          groupedHits={groupedHits}
          onHintClick={handleHintClick}
          regionLocale={regionLocale}
        />
      )}
    </S.Wrapper>
  );
};
